import { useCallback, useEffect, useMemo } from 'react';

import {
    ACQUISITION_FEE_ABBR,
    GENERIC_LAST_FIELD_CHANGED_ERROR,
    ACQUISITION_FEE_OPTION_LABEL_INTERSTATE
} from '../../../constants';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as offerActionTypes from '@makemydeal/dr-dash-store/actionTypes/offerActionTypes';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import { dealLockHooks } from '../../../hooks';
import { useFieldOverrideIndicator } from '../../../hooks/useFieldOverrideIndicator';
import { useHeldValue } from '../../../hooks/useHeldSelector';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { usePaymentCall } from '../../../utils/usePaymentCall';
import { formatNumberAndCents, kebabCase } from '@makemydeal/dr-common-utils';
import { useSelector, useDispatch } from 'react-redux';
import type { RSAAAction } from 'redux-api-middleware';
import type { DealTabs } from '@makemydeal/dr-shared-types';

const ACQ_FEE_OVERRIDE_INDICATOR_TEST_ID = 'acq-fee-override-indicator';

export const useAcquisitionFeeField = ({ tab }: { tab?: DealTabs }) => {
    const { updateAcqFeeOverride } = offerActionCreators;
    const dispatch = useDispatch();
    const isLocked = dealLockHooks.useIsDealLocked();
    const failed = useSelector(deskingSelectors.wasTheLastFailure(offerActionTypes.UPDATED_ACQFEE_OVERRIDE));
    const failedMessage = useMemo(() => (failed ? GENERIC_LAST_FIELD_CHANGED_ERROR : undefined), [failed]);

    const checked = useHeldValue(useSelector(offerSelectors.getAcqFeeUpFrontOverrideWithFallback));
    const heldAmount = useHeldValue(useSelector(offerSelectors.getAcqFeeOverrideWithFallback));
    const updateAmount = useCallback((amount: number) => updateAcqFeeOverride(amount, checked), [updateAcqFeeOverride, checked]);
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);

    const fieldName = kebabCase(ACQUISITION_FEE_ABBR);

    const {
        enabled: overrideIndicatorEnabled,
        show: showOverrideIndicator,
        message: overrideMessage,
        recordManualOverride
    } = useFieldOverrideIndicator('acqFee');

    const { value, handleChange, handleBlur, handleKeyDown } = usePaymentCall(
        offerSelectors.getAcqFeeOverrideWithFallback,
        updateAmount,
        {
            formatValueToString: (dollars: any) => formatNumberAndCents(dollars),
            onUserEdit: recordManualOverride,
            holdWhileTermResponsePending: true
        }
    );

    // istanbul ignore next
    const onChange = (e: any) => {
        dispatch(interactionAnalyticsActionCreators.acqFeeUpfrontCheckbox());
        /* The checked value comes back as part of e.target.value due to utilizing the NumericInput interstate component */
        dispatch(
            offerActionCreators.updateAcqFeeUpFrontOverride(
                e.target.value.checked === undefined ? (e as any).domEvent.target.checked : e.target.value.checked,
                heldAmount
            ) as unknown as RSAAAction<any, unknown, unknown>
        );
    };

    useEffect(() => {
        if (isDealScreenRedDotErrorsEnabled && tab) {
            dispatch(dealScreenErrorActionCreators.setFieldError(tab, fieldName, failed));
        }
    }, [isDealScreenRedDotErrorsEnabled, dispatch, failed, tab]);

    return {
        label: ACQUISITION_FEE_ABBR,
        upfrontLabel: ACQUISITION_FEE_OPTION_LABEL_INTERSTATE,
        overrideIndicatorProps: {
            enabled: overrideIndicatorEnabled,
            show: showOverrideIndicator,
            message: overrideMessage,
            testId: ACQ_FEE_OVERRIDE_INDICATOR_TEST_ID
        },
        inputProps: {
            id: fieldName,
            hasError: failed,
            errorMessage: failedMessage,
            name: fieldName,
            placeholder: '$0',
            value,
            'data-testid': fieldName,
            onChange: handleChange,
            onBlur: (e: any) => {
                dispatch(interactionAnalyticsActionCreators.acqFeeTextInput());
                handleBlur(e);
            },
            allowDecimal: true,
            decimalMaxLength: 2,
            decimalMinLength: 0,
            disabled: isLocked,
            inputPrefix: '$',
            autoInsertCommas: true,
            size: 'sm' as const,
            onKeyDown: handleKeyDown
        },
        upfrontProps: {
            'data-testid': fieldName,
            id: fieldName,
            name: fieldName,
            onChange,
            checked,
            value: '1',
            disabled: isLocked
        }
    };
};
